import type { Locale, MessageParams, NounTable } from './types';
import { LOCALES } from './types';
import { formatMessage, type FormatContext } from './format';

import catalogDe from './catalog/de.json';
import catalogEn from './catalog/en.json';
// Die Hinweistexte liegen getrennt, weil sie den Grossteil des Katalogs
// ausmachen und beim Erweitern des Mysteriums als Block wachsen.
import cluesDe from './catalog/clues.de.json';
import cluesEn from './catalog/clues.en.json';
import voyageDe from './catalog/voyage.de.json';
import voyageEn from './catalog/voyage.en.json';
import voyage2De from './catalog/voyage2.de.json';
import voyage2En from './catalog/voyage2.en.json';
import adviserDe from './catalog/adviser.de.json';
import voyage3De from './catalog/voyage3.de.json';
import voyage4De from './catalog/voyage4.de.json';
import personalDe from './catalog/personal.de.json';
import adviserEn from './catalog/adviser.en.json';
import voyage3En from './catalog/voyage3.en.json';
import voyage4En from './catalog/voyage4.en.json';
import personalEn from './catalog/personal.en.json';
import nounsDe from './nouns/de.json';
import nounsEn from './nouns/en.json';

export type { Locale, MessageParams, NounTable } from './types';
export { LOCALES } from './types';

type Catalog = Readonly<Record<string, string>>;

const CATALOGS: Readonly<Record<Locale, Catalog>> = {
  de: { ...catalogDe, ...cluesDe, ...voyageDe, ...voyage2De, ...voyage3De, ...voyage4De, ...personalDe, ...adviserDe },
  en: { ...catalogEn, ...cluesEn, ...voyageEn, ...voyage2En, ...voyage3En, ...voyage4En, ...personalEn, ...adviserEn },
};

const NOUNS: Readonly<Record<Locale, NounTable>> = {
  de: nounsDe as NounTable,
  en: nounsEn as NounTable,
};

export const DEFAULT_LOCALE: Locale = 'de';

let current: Locale = DEFAULT_LOCALE;

export function setLocale(locale: Locale): void {
  current = locale;
}

export function getLocale(): Locale {
  return current;
}

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/** Leitet die Sprache aus der Browserumgebung ab, mit Deutsch als Rueckfallebene. */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;

  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.split('-')[0]?.toLowerCase();
    if (base !== undefined && isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

/**
 * Uebersetzt einen Schluessel. Fehlt er, wird der Schluessel selbst in
 * Klammern zurueckgegeben — im Spiel sofort sichtbar, und der
 * Katalogtest schlaegt darauf an, bevor es je dorthin kommt.
 */
export function t(key: string, params: MessageParams = {}, locale: Locale = current): string {
  const template = CATALOGS[locale][key];
  if (template === undefined) {
    return `⟨${key}⟩`;
  }

  const ctx: FormatContext = { locale, nouns: NOUNS[locale] };
  return formatMessage(template, params, ctx);
}

/** Direkter Zugriff fuer Tests und Werkzeuge. */
export function getCatalog(locale: Locale): Catalog {
  return CATALOGS[locale];
}

export function getNouns(locale: Locale): NounTable {
  return NOUNS[locale];
}
